import React, { useEffect, useState } from "react";
import { Modal, Tabs, Descriptions, Tag, Empty } from "antd";
import {
  ToolOutlined,
  SettingOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import type { ServiceResponse } from "@OrianaTypes";
import { useGetEntityFilesQuery } from "../../store/api/fileApi";
import UploadedFilesList from "./UploadedFilesList";
import { formatLabel, getStatusColorByType } from "../../utils";

const PRECOMMISSIONING_ENTITY_TYPE = "precommissioning";
const COMMISSIONING_ENTITY_TYPE = "commissioning";
const WARRANTY_ENTITY_TYPE = "warranty_certificate";

export type ServiceDetailsTab =
  | "precommissioning"
  | "commissioning"
  | "warranty";

interface ServiceDetailsModalProps {
  visible: boolean;
  onClose: () => void;
  data: ServiceResponse | null;
  initialTab?: ServiceDetailsTab;
}

const ServiceDetailsModal: React.FC<ServiceDetailsModalProps> = ({
  visible,
  onClose,
  data,
  initialTab = "precommissioning",
}) => {
  const [activeTab, setActiveTab] = useState<ServiceDetailsTab>(initialTab);

  const serviceId = data?.serviceId != null ? String(data.serviceId) : "";
  const { data: precommissioningFilesRes, isLoading: loadingPrecommissioning } = useGetEntityFilesQuery(
    { entityType: PRECOMMISSIONING_ENTITY_TYPE, entityId: serviceId },
    { skip: !serviceId || !visible }
  );
  const { data: commissioningFilesRes, isLoading: loadingCommissioning } = useGetEntityFilesQuery(
    { entityType: COMMISSIONING_ENTITY_TYPE, entityId: serviceId },
    { skip: !serviceId || !visible }
  );
  const { data: warrantyFilesRes, isLoading: loadingWarranty } = useGetEntityFilesQuery(
    { entityType: WARRANTY_ENTITY_TYPE, entityId: serviceId },
    { skip: !serviceId || !visible }
  );
  const precommissioningFiles = precommissioningFilesRes?.data ?? [];
  const commissioningFiles = commissioningFilesRes?.data ?? [];
  const warrantyFiles = warrantyFilesRes?.data ?? [];

  // Update active tab when initialTab changes
  useEffect(() => {
    if (visible) {
      setActiveTab(initialTab);
    }
  }, [visible, initialTab]);

  // Pre-Commissioning Tab Content (Section 1)
  const renderPreCommissioningDetails = () => {
    if (!data) {
      return <Empty description="No service data available" />;
    }

    return (
      <div>
        <Descriptions
          bordered
          column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }}
          size="small"
          labelStyle={{
            fontWeight: 600,
            backgroundColor: "#fafafa",
            width: "200px",
          }}
        >
          <Descriptions.Item label="Service ID">
            <Tag color="purple">#{data.serviceId}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Dispatch ID">
            <Tag color="blue">#{data.dispatchId}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Serial Number">
            <Tag color="geekblue">{data.serialNumber}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="PC Contact">
            {data.pcContact || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Service Engineer Assigned">
            {data.assignedServiceEngineer || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="PPM/Checklist">
            {data.ppmChecklist || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="PPM Sheet Received from Client">
            {data.ppmSheetReceivedFromClient || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="PPM Checklist Shared with OEM">
            {data.ppmChecklistSharedWithOem || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="PPM Ticket No from OEM">
            {data.ppmTickedNoFromOem || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="PPM Confirmation Status">
            {data.ppmConfirmationStatus ? (
              <Tag
                color={getStatusColorByType(
                  data.ppmConfirmationStatus,
                  "clearance"
                )}
              >
                {formatLabel(data.ppmConfirmationStatus)}
              </Tag>
            ) : (
              "-"
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Pre-Commissioning Status">
            {data.preCommissioningStatus ? (
              <Tag
                color={getStatusColorByType(
                  data.preCommissioningStatus,
                  "service"
                )}
              >
                {formatLabel(data.preCommissioningStatus)}
              </Tag>
            ) : (
              "-"
            )}
          </Descriptions.Item>
          <Descriptions.Item label="OEM Comments" span={2}>
            {data.oemComments || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Remarks" span={2}>
            {data.remarks || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Last Updated">
            {data.preCommissioningUpdatedAt
              ? new Date(data.preCommissioningUpdatedAt).toLocaleString()
              : "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Created At">
            {data.createdAt ? new Date(data.createdAt).toLocaleString() : "-"}
          </Descriptions.Item>
        </Descriptions>
        {/* Pre-Commissioning Documents - same style as Delivery Confirmation view */}
        <UploadedFilesList
          files={precommissioningFiles}
          isLoading={loadingPrecommissioning}
          title="Pre-Commissioning Documents"
        />
      </div>
    );
  };

  // Commissioning Tab Content (Section 2)
  const renderCommissioningDetails = () => {
    if (!data) {
      return <Empty description="No service data available" />;
    }

    return (
      <div>
        <Descriptions
          bordered
          column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }}
          size="small"
          labelStyle={{
            fontWeight: 600,
            backgroundColor: "#fafafa",
            width: "200px",
          }}
        >
          <Descriptions.Item label="Service ID">
            <Tag color="purple">#{data.serviceId}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Dispatch ID">
            <Tag color="blue">#{data.dispatchId}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Serial Number">
            <Tag color="geekblue">{data.serialNumber}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Product">
            {formatLabel(data.productName) || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Commissioning Status">
            {data.commissioningStatus ? (
              <Tag
                color={getStatusColorByType(data.commissioningStatus, "service")}
              >
                {formatLabel(data.commissioningStatus)}
              </Tag>
            ) : (
              "-"
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Commissioning Date">
            {data.commissioningDate || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="ECD From Client">
            {data.commissioningEcdFromClient || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Service Ticket No">
            {data.commissioningServiceTicketNo || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Issues" span={2}>
            {data.commissioningIssues || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Solution" span={2}>
            {data.commissioningSolution || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Remarks" span={2}>
            {data.commissioningRemarks || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Last Updated">
            {data.commissioningUpdatedAt
              ? new Date(data.commissioningUpdatedAt).toLocaleString()
              : "-"}
          </Descriptions.Item>
        </Descriptions>
        <UploadedFilesList
          files={commissioningFiles}
          isLoading={loadingCommissioning}
          title="Commissioning Documents"
        />
      </div>
    );
  };

  // Warranty Certificate Tab Content (Section 3)
  const renderWarrantyDetails = () => {
    if (!data) {
      return <Empty description="No service data available" />;
    }

    return (
      <div>
        <Descriptions
          bordered
          column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }}
          size="small"
          labelStyle={{
            fontWeight: 600,
            backgroundColor: "#fafafa",
            width: "200px",
          }}
        >
          <Descriptions.Item label="Service ID">
            <Tag color="purple">#{data.serviceId}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Dispatch ID">
            <Tag color="blue">#{data.dispatchId}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Serial Number">
            <Tag color="geekblue">{data.serialNumber}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Product">
            {formatLabel(data.productName) || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Warranty Status">
            {data.warrantyStatus ? (
              <Tag color={getStatusColorByType(data.warrantyStatus, "service")}>
                {formatLabel(data.warrantyStatus)}
              </Tag>
            ) : (
              "-"
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Certificate No">
            {data.warrantyCertificateNo || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Issue Date">
            {data.warrantyIssueDate || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Warranty Start Date">
            {data.warrantyStartDate || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Warranty End Date">
            {data.warrantyEndDate || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Last Updated">
            {data.warrantyUpdatedAt
              ? new Date(data.warrantyUpdatedAt).toLocaleString()
              : "-"}
          </Descriptions.Item>
        </Descriptions>
        <UploadedFilesList
          files={warrantyFiles}
          isLoading={loadingWarranty}
          title="Warranty Certificate Documents"
        />
      </div>
    );
  };

  const tabItems = [
    {
      key: "precommissioning",
      label: (
        <span>
          <ToolOutlined /> Pre-Commissioning
        </span>
      ),
      children: renderPreCommissioningDetails(),
    },
    {
      key: "commissioning",
      label: (
        <span>
          <SettingOutlined /> Commissioning
        </span>
      ),
      children: renderCommissioningDetails(),
    },
    {
      key: "warranty",
      label: (
        <span>
          <SafetyCertificateOutlined /> Warranty Certificate
        </span>
      ),
      children: renderWarrantyDetails(),
    },
  ];

  return (
    <Modal
      title={`Service Details - #${data?.serviceId || ""}`}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={900}
      destroyOnClose
    >
      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key as ServiceDetailsTab)}
        items={tabItems}
        style={{ marginTop: "8px" }}
      />
    </Modal>
  );
};

export default ServiceDetailsModal;
